import { createContext, PropsWithChildren, useContext, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { isSameDay, startOfMonth, parseISO } from 'date-fns';
import { useQueryRouter } from '@guest-widgets/shared/src/components/Router/QueryRouter';
import { GuestProduct } from '@guest-widgets/shared/src/types/product/guestProduct';
import { UpsellItem } from '@guest-widgets/shared/src/types/product/upsellItem';

import { useTimeslotApi } from '../apiContext/timeslot/useTimeslotApi';
import { LoadingErrorWrapper } from '../../LoadingErrorWrapper';
import { CartContext, useCart } from '../cartContext/cartContext';
import { useLoadingError } from '../../hooks/useLoadingError';
import { isUpsell } from '../../Product/Guest/productInSelectionContext';

import { AvailabilityTime, AvailabilityTimeDayTicket, Timeslots, TimeslotsDay } from './timeslots';

type BasicTimeslotFilters = {
  numberOfMonths?: number;
  dateFrom?: Date;
  nextAvailableDay?: boolean;
  showSoldOut?: boolean;
};

export interface TimeslotContext {
  timeslot: UseQueryResult<Timeslots, unknown>;
  filters: BasicTimeslotFilters;
  product: GuestProduct;
  setFilters: (filter: BasicTimeslotFilters) => void;
  getTimeslotDayByDate: (date: Date) => TimeslotsDay | undefined;
}

const timeslotContext = createContext({} as TimeslotContext);

export interface TimeslotContextProviderProps {
  initialData?: Timeslots;
  product: GuestProduct | UpsellItem;
}

export const TimeslotsContextProvider = ({
  children,
  initialData,
  product,
}: PropsWithChildren<TimeslotContextProviderProps>) => {
  const { trafficLights, displayTime, productId } = product || {};
  let start: string | undefined;
  const cartApi = useCart() as CartContext;
  const productIsUpsell = isUpsell(product);
  const queryRouter = useQueryRouter();
  const urlDate = queryRouter.get('re-filter-date-from');
  let urlDay: Date | undefined;
  if (urlDate) {
    try {
      // Ensure the date is in ISO format (YYYY-MM-DD)
      const [year, month, day] = urlDate.split('-').map((num: string) => num.padStart(2, '0'));
      const isoDate = `${year}-${month}-${day}`;
      const parsedDate = parseISO(isoDate);
      if (!isNaN(parsedDate.getTime())) {
        urlDay = new Date(parsedDate.getTime());
      }
    } catch (error) {
      console.warn('Invalid date format in URL:', urlDate);
    }
  }
  const [filters, setFilters] = useState<BasicTimeslotFilters>({
    showSoldOut: displayTime?.showUnavailable,
    dateFrom: urlDay ? startOfMonth(urlDay) : undefined,
    nextAvailableDay: true,
  });
  const hasProduct = !!productId;

  if (productIsUpsell) {
    start = product.start;
  }

  const timeslot = useTimeslotApi({
    initialData,
    productId,
    // If the product is an upsell and have a start date (from cart), we need to request the timeslots from this date
    dateFrom: productIsUpsell && start ? startOfMonth(new Date(start)) : undefined,
    ...filters,
    enabled: hasProduct,
    trafficLights,
    cart: cartApi.cartWithSteps?.data?.cart,
    ...(productIsUpsell ? { upsellParentLineId: product.parent.lineId } : {}),
  });

  const { isLoading, errorCode } = useLoadingError(
    timeslot.error,
    timeslot.fetchStatus,
    timeslot.status
  );

  const getTimeslotDayByDate = (date: Date) => {
    return timeslot.data?.days.find(({ day }) => isSameDay(day, date));
  };

  const value: TimeslotContext = {
    timeslot,
    filters,
    product,
    setFilters: (newFilters) => setFilters((current) => ({ ...current, ...newFilters })),
    getTimeslotDayByDate,
  };

  return (
    <timeslotContext.Provider value={value}>
      <LoadingErrorWrapper
        isError={timeslot.isError}
        isLoading={isLoading}
        errorCode={errorCode}
        custom={productIsUpsell ? 'product' : undefined}
      >
        {children}
      </LoadingErrorWrapper>
    </timeslotContext.Provider>
  );
};

export const useTimeslots = () => useContext(timeslotContext);

export const isDayTicketTimeslot = (
  timeslot: AvailabilityTime
): timeslot is AvailabilityTimeDayTicket => !!(timeslot as AvailabilityTimeDayTicket).timeRange;

export const getTimeslotTimes = (
  timeslot: AvailabilityTime
): { startTime: Date; endTime: Date } => {
  if (isDayTicketTimeslot(timeslot)) {
    return {
      startTime: timeslot.timeRange.startTime,
      endTime: timeslot.timeRange.endTime,
    };
  }
  return {
    startTime: timeslot.time,
    endTime: timeslot.time,
  };
};
